import * as Yup from "yup";
import { validMobileFormatByOwnPrefixTest } from "../../../utils/phoneValidation";

// The booking form's validation schema, moved unchanged from Bookings.jsx.
//   isSportsService / isCarService / isSalonService   industry (address becomes optional, wording changes)
//   partners   the vendor's profiles - a Utilstop profile may use a number from any country
//   currency   "INR" | "AED" - the profile's country, which the phone number must match
//   countries  [{ country_id, name }] - decides State vs Emirate for home-service vendors
export function buildBookingSchema({ isSportsService, isCarService, isSalonService, partners, currency, countries }) {
  const addressOptional = isSportsService || isCarService || isSalonService;
  const person = isSportsService ? "Player" : "Customer";

  return Yup.object().shape({
    partner: Yup.string().required("Profile is required"),
    phoneNumber: Yup.string()
      .required("Mobile Number is required")
      .test("country-specific-length", function (value) {
        if (!value) return true;
        const cleanedValue = value.replace(/\D/g, "");
        if (cleanedValue.startsWith("91")) {
          return (
            cleanedValue.length >= 12 ||
            this.createError({
              message: "Please enter a valid Indian mobile number (min 10 digits)"
            })
          );
        }
        if (cleanedValue.startsWith("971")) {
          return (
            cleanedValue.length >= 12 ||
            this.createError({
              message: "Please enter a valid UAE mobile number (min 9 digits)"
            })
          );
        }
        // Default: minimum 10 digits (including country code)
        return (
          cleanedValue.length >= 10 ||
          this.createError({
            message: "Please enter a valid mobile number (min 10 digits)"
          })
        );
      })
      .test("country-match-profile", function (value) {
        if (!value) return true;

        // Utilstop profile bookings can use a number from any country -
        // only enforce the profile-country match for non-Utilstop profiles.
        const selectedPartnerId = Number(this.parent?.partner);
        const selectedPartner = partners?.find((p) => Number(p.id) === selectedPartnerId);
        const isUtilstopProfile = selectedPartner?.partner_name?.trim().toLowerCase() === "utilstop";
        if (isUtilstopProfile) return true;

        const cleanedValue = value.replace(/\D/g, "");
        const wrongPrefix = currency === "INR" ? "971" : "91";
        const expectedCountry = currency === "INR" ? "India" : "UAE";
        if (cleanedValue.startsWith(wrongPrefix)) {
          return this.createError({
            message: `Please select a ${expectedCountry} number — this matches your profile's country.`
          });
        }
        return true;
      })
      .test("valid-mobile-format", validMobileFormatByOwnPrefixTest()),
    userName: Yup.string()
      .required(`${person} name is required`)
      .min(2, `${person} name must be at least 2 characters`)
      .max(20, `${person} name must not exceed 20 characters`),

    house_no: addressOptional
      ? Yup.string().max(100, "Street address must not exceed 100 characters")
      : Yup.string()
          .required("Street / House no. is required")
          .max(100, "Street address must not exceed 100 characters"),

    area: addressOptional ? Yup.string() : Yup.string().required("Area is required"),
    location: addressOptional ? Yup.string() : Yup.string().required("Address is required"),
    state: addressOptional
      ? Yup.string()
      : Yup.string().when("country", {
          is: (country) => {
            const selected = (countries || []).find((c) => Number(c.country_id) === Number(country));
            return selected?.name?.toLowerCase().includes("india");
          },
          then: (schema) => schema.required("State is required"),
          otherwise: (schema) => schema.required("Emirate is required")
        }),
    country: addressOptional ? Yup.string() : Yup.string().required("Country is required"),

    lat: Yup.string()
      .nullable()
      .notRequired()
      .test("is-valid-latitude", "Invalid latitude value", (value) => {
        if (!value) return true;
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue >= -90 && numValue <= 90;
      }),

    lng: Yup.string()
      .nullable()
      .notRequired()
      .test("is-valid-longitude", "Invalid longitude value", (value) => {
        if (!value) return true;
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue >= -180 && numValue <= 180;
      }),
    email: Yup.string()
      .nullable()
      .test("valid-email", "Enter valid email (.com, .in, .co.in only)", (value) => {
        if (!value) return true;
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|co\.in)$/;
        return emailRegex.test(value);
      })
  });
}
